import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Multiplies all provided numbers together
 *
 * @param expressions - List of expressions to multiply together
 * @example
 * { '*': [ 6, 2, 1 ] }
 * // ->
 * 12
 */
export interface Multiply<out T = any> {
  /** {@link Multiply} */
  readonly [Key.Multiply]: T extends number
    ? readonly Expression<number>[]
    : never
}
export namespace Multiply {
  export const isMultiply: TypeChecker<Multiply> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<Multiply>({
        [Key.Multiply]: CommonArgs.isListOfExpressions as TypeChecker<
          Multiply[Key.Multiply]
        >,
      }),
  ).usingAlias('Fn.Multiply')
  export const assertIsMultiply: TypeAsserter<Multiply> =
    TypeAsserter.Factory.from(isMultiply)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Multiply,
    readonly expressionNode.Node<number>[]
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type {
      return (
        this.arg.find(node =>
          common.value.types.Intrinsic.ForNumber.isForNumber(node.type),
        )?.type ?? common.value.types.Intrinsic.Decimal
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Multiply, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Multiply>['expressionsInArgs'] = fn =>
    fn[Key.Multiply]
  export const HELPER: FnHelper<Multiply> = {
    createNode: standardNodeCreator.usingExpressionList({
      // If there is nothing to multiply, the product is 1
      emptyValue: 1,
      finalizeArgs: (args: readonly expressionNode.Node<number>[], stash) => [
        ...args,
        // If the value product is not 1, we need to include it in the args
        ...(stash.product === 1
          ? []
          : [new expressionNode.ForValue(stash.product)]),
      ],
      getArgExpressions: expressionsInArgs,
      // We just multiply together all value nodes to create one value
      // expression at the end
      handleValue: (node, stash) => {
        stash.product *= Number(node.value)
      },
      nodeClass: Node,
      setupStash: () => ({ product: 1 }),
    }),
    expressionsInArgs,
    typeChecker: isMultiply,
  }
}
